/**
 * Sprite Pool Manager
 * Manages a pool of sprites that can be reused instead of destroyed/recreated
 * This makes undo/redo instant for all sprite types including GIFs
 */
class SpritePoolManager {
  constructor() {
    // Map of elementId to sprite data
    this.spritePool = new Map();
    this.maxPoolSize = 100; // Maximum sprites to keep in memory
  }

  /**
   * Store a sprite in the pool instead of destroying it
   * @param {string} elementId - The element ID
   * @param {Object} sprite - The PIXI sprite
   * @param {Object} interactiveArea - The interactive area
   * @param {Object} elementData - The element data snapshot
   */
  storeSprite(elementId, sprite, interactiveArea, elementData) {
    if (!sprite) return;

    // Hide the sprite instead of destroying
    sprite.visible = false;
    if (interactiveArea) {
      interactiveArea.visible = false;
    }

    // Stop any animations/videos
    if (sprite.isGif && sprite.playing) {
      sprite.stop();
    }
    if (sprite.videoElement) {
      sprite.videoElement.pause();
    }

    // Store in pool
    this.spritePool.set(elementId, {
      sprite,
      interactiveArea,
      elementData,
      timestamp: Date.now()
    });

    // Manage pool size
    if (this.spritePool.size > this.maxPoolSize) {
      // Remove oldest sprites
      const sortedEntries = Array.from(this.spritePool.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toRemove = sortedEntries.slice(0, this.spritePool.size - this.maxPoolSize);
      toRemove.forEach(([id, data]) => {
        this.destroyPooledSprite(id);
      });
    }

    console.log(`Stored sprite ${elementId} in pool (pool size: ${this.spritePool.size})`);
  }

  /**
   * Retrieve a sprite from the pool if available
   * @param {string} elementId - The element ID
   * @returns {Object|null} - The sprite data or null if not in pool
   */
  getSprite(elementId) {
    const pooledData = this.spritePool.get(elementId);
    if (pooledData) {
      // Update timestamp for LRU
      pooledData.timestamp = Date.now();
      
      // Make visible again
      pooledData.sprite.visible = true;
      if (pooledData.interactiveArea) {
        pooledData.interactiveArea.visible = true;
      }

      console.log(`Retrieved sprite ${elementId} from pool`);
      return pooledData;
    }
    return null;
  }

  /**
   * Check if a sprite exists in the pool
   * @param {string} elementId - The element ID
   * @returns {boolean}
   */
  hasSprite(elementId) {
    return this.spritePool.has(elementId);
  }

  /**
   * Remove and destroy a sprite from the pool
   * @param {string} elementId - The element ID
   */
  destroyPooledSprite(elementId) {
    const pooledData = this.spritePool.get(elementId);
    if (!pooledData) return;

    const { sprite, interactiveArea } = pooledData;

    // Properly destroy the sprite
    if (sprite) {
      // Clean up video
      if (sprite.videoElement) {
        sprite.videoElement.pause();
        sprite.videoElement.src = "";
        sprite.videoElement = null;
      }

      // Clean up audio
      if (sprite._cleanupAudio) {
        sprite._cleanupAudio();
      }

      // Clean up AI placeholder animation
      if (sprite._cleanupAnimation) {
        sprite._cleanupAnimation();
      }

      // Remove from parent
      if (sprite.parent) {
        sprite.parent.removeChild(sprite);
      }

      // Destroy sprite
      sprite.destroy({
        children: true,
        texture: false, // Keep textures cached
        baseTexture: false
      });
    }

    // Destroy interactive area
    if (interactiveArea) {
      if (interactiveArea.parent) {
        interactiveArea.parent.removeChild(interactiveArea);
      }
      interactiveArea.destroy({
        children: true,
        texture: true,
        baseTexture: true
      });
    }

    this.spritePool.delete(elementId);
    // console.log(`Destroyed pooled sprite ${elementId}`);
  }

  /**
   * Clear all sprites from the pool
   */
  clearPool() {
    // console.log(`Clearing sprite pool (${this.spritePool.size} sprites)`);
    this.spritePool.forEach((data, id) => {
      this.destroyPooledSprite(id);
    });
    this.spritePool.clear();
  }

  /**
   * Get pool statistics
   */
  getStats() {
    return {
      size: this.spritePool.size,
      maxSize: this.maxPoolSize,
      sprites: Array.from(this.spritePool.keys())
    };
  }
}

// Create singleton instance
const spritePoolManager = new SpritePoolManager();

export default spritePoolManager;